import {
  useEffect, useRef, useState, useCallback,
} from "react";
import { cn } from "@/shared/lib/utils";

type Segment = "day" | "month" | "year";

type SegmentedDateInputProps = {
  value: string
  onChange: (value: string) => void
  onCommit: () => void
  onFocus?: () => void
  disabled?: boolean
  className?: string
  ariaLabel?: string
};

const SEGMENTS = {
  day: {
    start: 0, end: 2, max: 2, placeholder: "jj",
  },
  month: {
    start: 3, end: 5, max: 2, placeholder: "mm",
  },
  year: {
    start: 6, end: 10, max: 4, placeholder: "aaaa",
  },
} as const;

const SEGMENT_ORDER: Segment[] = ["day", "month", "year"];

type SegmentValues = { day: string, month: string, year: string };

const getMaxDaysInMonth = (month: number, year: number): number => {
  if (month < 1 || month > 12) return 31;
  if (year < 1) return 31;
  return new Date(year, month, 0).getDate();
};

const parseSegments = (value: string): SegmentValues => {
  const match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{1,4})$/);
  if (!match) return { day: "", month: "", year: "" };
  const day = match[1] === "00" ? "" : match[1];
  const month = match[2] === "00" ? "" : match[2];
  const year = match[3] === "0000" ? "" : match[3];
  return { day, month, year };
};

const formatSegments = (segments: SegmentValues): string => {
  const d = segments.day ? segments.day.padStart(2, "0") : "00";
  const m = segments.month ? segments.month.padStart(2, "0") : "00";
  const y = segments.year ? segments.year.padStart(4, "0") : "0000";
  return `${d}/${m}/${y}`;
};

const clampDay = (day: number, month: number, year: number): number => {
  const max = getMaxDaysInMonth(month, year);
  return Math.min(day, max);
};

const wrapValue = (current: number, direction: 1 | -1, min: number, max: number): number => {
  if (current === 0) return direction === 1 ? min : max;
  const next = current + direction;
  if (next > max) return min;
  if (next < min) return max;
  return next;
};

const incrementDay = (segments: SegmentValues, direction: 1 | -1): SegmentValues => {
  const month = Number(segments.month) || 1;
  const year = Number(segments.year) || new Date().getFullYear();
  const max = getMaxDaysInMonth(month, year);
  const current = Number(segments.day) || 0;
  const newDay = wrapValue(current, direction, 1, max);
  return { ...segments, day: String(newDay).padStart(2, "0") };
};

const incrementMonth = (segments: SegmentValues, direction: 1 | -1): SegmentValues => {
  const current = Number(segments.month) || 0;
  const newMonth = wrapValue(current, direction, 1, 12);
  const year = Number(segments.year) || new Date().getFullYear();
  const day = Number(segments.day) || 0;
  const clampedDay = day > 0 ? clampDay(day, newMonth, year) : day;
  return {
    ...segments,
    month: String(newMonth).padStart(2, "0"),
    day: clampedDay > 0 ? String(clampedDay).padStart(2, "0") : segments.day,
  };
};

const incrementYear = (segments: SegmentValues, direction: 1 | -1): SegmentValues => {
  const current = Number(segments.year) || 0;
  const newYear = current === 0
    ? new Date().getFullYear()
    : Math.max(1900, Math.min(2100, current + direction));
  const month = Number(segments.month) || 1;
  const day = Number(segments.day) || 0;
  const clampedDay = day > 0 ? clampDay(day, month, newYear) : day;
  return {
    ...segments,
    year: String(newYear).padStart(4, "0"),
    day: clampedDay > 0 ? String(clampedDay).padStart(2, "0") : segments.day,
  };
};

const processDigitForDay = (
  buffer: string,
  updateSegment: (seg: Segment, val: string) => void,
  moveSegment: (dir: 1 | -1) => void,
): boolean => {
  if (buffer.length === 1 && Number(buffer) > 3) {
    updateSegment("day", `0${buffer}`);
    moveSegment(1);
    return true;
  }
  if (buffer.length >= 2) {
    const val = Math.max(1, Math.min(Number(buffer), 31));
    updateSegment("day", String(val).padStart(2, "0"));
    moveSegment(1);
    return true;
  }
  return false;
};

const processDigitForMonth = (
  buffer: string,
  updateSegment: (seg: Segment, val: string) => void,
  moveSegment: (dir: 1 | -1) => void,
): boolean => {
  if (buffer.length === 1 && Number(buffer) > 1) {
    updateSegment("month", `0${buffer}`);
    moveSegment(1);
    return true;
  }
  if (buffer.length >= 2) {
    const val = Math.max(1, Math.min(Number(buffer), 12));
    updateSegment("month", String(val).padStart(2, "0"));
    moveSegment(1);
    return true;
  }
  return false;
};

const processDigitForYear = (
  buffer: string,
  updateSegment: (seg: Segment, val: string) => void,
): boolean => {
  if (buffer.length >= 4) {
    updateSegment("year", buffer.slice(0, 4));
    return true;
  }
  return false;
};

export const SegmentedDateInput = ({
  value,
  onChange,
  onCommit,
  onFocus: onFocusProp,
  disabled = false,
  className,
  ariaLabel,
}: SegmentedDateInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [activeSegment, setActiveSegment] = useState<Segment | null>(null);
  const typingBufferRef = useRef("");
  const commitOnBlurRef = useRef(true);
  const isMouseDownRef = useRef(false);

  const segments = parseSegments(value);

  const displayValue = (() => {
    const d = segments.day ? segments.day.padStart(2, "0") : "jj";
    const m = segments.month ? segments.month.padStart(2, "0") : "mm";
    const y = segments.year ? segments.year.padStart(4, "0") : "aaaa";
    return `${d}/${m}/${y}`;
  })();

  const highlightSegment = useCallback((segment: Segment) => {
    const seg = SEGMENTS[segment];
    requestAnimationFrame(() => {
      inputRef.current?.setSelectionRange(seg.start, seg.end);
    });
  }, []);

  useEffect(() => {
    if (activeSegment) {
      highlightSegment(activeSegment);
    }
  }, [activeSegment, value, highlightSegment]);

  const updateSegment = useCallback(
    (segment: Segment, newValue: string) => {
      const updated = { ...segments, [segment]: newValue };
      onChange(formatSegments(updated));
    },
    [segments, onChange],
  );

  const moveSegment = useCallback(
    (direction: 1 | -1) => {
      if (!activeSegment) return;
      const idx = SEGMENT_ORDER.indexOf(activeSegment);
      const next = idx + direction;
      if (next >= 0 && next < SEGMENT_ORDER.length) {
        typingBufferRef.current = "";
        setActiveSegment(SEGMENT_ORDER[next]);
      }
    },
    [activeSegment],
  );

  const incrementSegment = useCallback(
    (direction: 1 | -1) => {
      if (!activeSegment) return;
      typingBufferRef.current = "";

      const incrementors = { day: incrementDay, month: incrementMonth, year: incrementYear };
      const updated = incrementors[activeSegment](segments, direction);
      onChange(formatSegments(updated));
    },
    [activeSegment, segments, onChange],
  );

  const handleDigit = useCallback(
    (digit: string) => {
      if (!activeSegment) return;

      const buffer = typingBufferRef.current + digit;

      const processors = {
        day: () => processDigitForDay(buffer, updateSegment, moveSegment),
        month: () => processDigitForMonth(buffer, updateSegment, moveSegment),
        year: () => processDigitForYear(buffer, updateSegment),
      };

      typingBufferRef.current = "";
      const handled = processors[activeSegment]();
      if (!handled) {
        typingBufferRef.current = buffer;
        updateSegment(activeSegment, buffer);
      }
    },
    [activeSegment, updateSegment, moveSegment],
  );

  const handleNavigationKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case "ArrowRight":
        e.preventDefault();
        moveSegment(1);
        return true;
      case "ArrowLeft":
        e.preventDefault();
        moveSegment(-1);
        return true;
      case "ArrowUp":
        e.preventDefault();
        incrementSegment(1);
        return true;
      case "ArrowDown":
        e.preventDefault();
        incrementSegment(-1);
        return true;
      case "Tab":
        if (!e.shiftKey && activeSegment !== "year") {
          e.preventDefault();
          moveSegment(1);
          return true;
        }
        if (e.shiftKey && activeSegment !== "day") {
          e.preventDefault();
          moveSegment(-1);
          return true;
        }
        return false;
      default:
        return false;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!activeSegment) return;

    if (handleNavigationKey(e)) return;

    switch (e.key) {
      case "Backspace":
      case "Delete":
        e.preventDefault();
        typingBufferRef.current = "";
        if (!segments[activeSegment]) {
          moveSegment(-1);
        }
        else {
          updateSegment(activeSegment, "");
        }
        break;
      case "Enter":
        e.preventDefault();
        commitOnBlurRef.current = false;
        inputRef.current?.blur();
        onCommit();
        break;
      default:
        if (/^\d$/.test(e.key)) {
          e.preventDefault();
          handleDigit(e.key);
        }
        break;
    }
  };

  const handleMouseDown = () => {
    isMouseDownRef.current = true;
  };

  const handleMouseUp = () => {
    isMouseDownRef.current = false;
    const pos = inputRef.current?.selectionStart ?? 0;
    typingBufferRef.current = "";
    if (pos <= 2) setActiveSegment("day");
    else if (pos <= 5) setActiveSegment("month");
    else setActiveSegment("year");
  };

  const handleFocus = () => {
    typingBufferRef.current = "";
    if (!isMouseDownRef.current) {
      setActiveSegment("day");
    }
    onFocusProp?.();
  };

  const handleBlur = () => {
    setActiveSegment(null);
    typingBufferRef.current = "";
    if (commitOnBlurRef.current) {
      onCommit();
    }
    commitOnBlurRef.current = true;
  };

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      aria-label={ariaLabel}
      value={displayValue}
      onChange={() => {}}
      onKeyDown={handleKeyDown}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder="jj/mm/aaaa"
      disabled={disabled}
      className={cn(
        "flex h-9 w-full rounded-md bg-transparent px-3 py-1 text-base transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className,
      )}
    />
  );
};
